import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { concatMap, switchMap, withLatestFrom, catchError, mergeMap, map } from 'rxjs/operators';
import { SplitPnrSuccess } from 'src/app/search-result/actions/pnr.actions';
import { LayoutService } from '../services/layout.service';
import { APIService } from 'src/app/core/api/general.service';
import { ContractError } from 'src/app/core/models/models';
import { AlertType } from 'src/app/core/enums/enums';
import { ISabreAirlineEPRLoginQuote } from 'src/app/core/models/booking-credentials.model';
import * as LayoutActions from '../actions/layout.actions';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class LayoutEffects {
  constructor(
    private actions$: Actions,
    private service: LayoutService,
    private api: APIService,
    private itineraryService: ItineraryService
  ) {}

  SplitSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SplitPnrSuccess),
      concatMap((action) => of(action).pipe(withLatestFrom(this.service.getBreadcrumb()))),
      switchMap(([action, oldBreadcrumb]) => {
        const page = oldBreadcrumb.slice(-1)[0];
        const breadcrumb = this.service.createBreadcrumb(action?.pnr?.code, page?.route);
        return [LayoutActions.ChangeBreadcrumb({ breadcrumb })];
      })
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.ChangePassword),
      switchMap((action) =>
        this.api.changePassword(action.request).pipe(
          switchMap((response) => {
            const successError: ContractError = {
              message: '',
              controllerName: '',
              actionName: '',
              stackTrace: '',
              errorCode: 997,
              alertType: AlertType.Success
            };
            return [
              LayoutActions.ChangePasswordSuccess(),
              LayoutActions.GenericError({ error: successError })
            ];
          }),
          catchError((response) => {
            return of(LayoutActions.GenericError({ error: response.error }));
          })
        )
      )
    )
  );

  LoadManualInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.LoadManualInfo),
      switchMap((payload) =>
        this.api.getManualByLanguage(payload.language).pipe(
          mergeMap(
            (response) =>
              response.status === 200 && [
                LayoutActions.LoadManualInfoSuccess({ manualInfo: response.body })
              ]
          ),
          catchError((response) => {
            return of(LayoutActions.GenericError({ error: response.error }));
          })
        )
      )
    )
  );

  DownloadManual$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.DownloadManual),
      switchMap((payload) =>
        this.api.downloadManualById(payload.id).pipe(
          mergeMap((response) => {
            return (
              response.status === 200 && [
                LayoutActions.DownloadManualSuccess({ manual: response.body })
              ]
            );
          }),
          catchError((response) => {
            return of(LayoutActions.GenericError({ error: response.error }));
          })
        )
      )
    )
  );

  SabreAirlineEPRLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.SabreAirlineEPRLogin),
      concatMap((action) => of(action).pipe(withLatestFrom(this.itineraryService.getPnrInfo()))),
      mergeMap(([action, pnrInfo]) => {
        const sabreAirlineEPRLoginQuote: ISabreAirlineEPRLoginQuote = {
          pnr: pnrInfo.code,
          country: action.sabreAirlineCredentialsData.office,
          gds: pnrInfo.gds,
          officeId: action.sabreAirlineCredentialsData.ipcc,
          transactionId: pnrInfo.transactionId,
          oversign: action.sabreAirlineCredentialsData
        };

        return this.api.postSabreAirlineEPRLogin(sabreAirlineEPRLoginQuote).pipe(
          map((response) => LayoutActions.SabreAirlineEPRLoginSuccess({ info: response.info })),
          catchError((error: HttpErrorResponse) =>
            of(LayoutActions.SabreAirlineEPRLoginError({ message: error.error.message }))
          )
        );
      })
    )
  );
}
